import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MapPin, CheckCircle2, Facebook, Instagram, 
  Edit2, Star, Share2, Mail, Phone,
  Building2, Home, Hammer, PenToolIcon,
  Award, Trophy, ChevronLeft, ChevronRight,
  Clock, AlertCircle, ExternalLink, Calendar,
  Users, Shield, Castle, TrendingUp, Eye, Heart, ThumbsUp
} from 'lucide-react';
import PropertyCardPro from './PropertyCardPro';
import PropertyCard from '../../components/PropertyCard';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/ui/toast/use-toast';
import RatingModal from '../rating/RatingModal';
import { GOOGLE_MAPS_API_KEY } from '../../config/keys';

// Constants
const CLOUDFLARE_PROFILES_URL = 'https://pub-2a10bd66114a4441b95e9249adee01cf.r2.dev';
const CLOUDFLARE_BANNERS_URL = 'https://pub-cdaec3d9feef44cf8950eee9a93266e4.r2.dev';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { 
      duration: 0.8, 
      ease: [0.25, 0.46, 0.45, 0.94]
    } 
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1
    }
  }
};

const cardHover = {
  rest: { scale: 1, y: 0 },
  hover: { 
    scale: 1.02, 
    y: -8,
    transition: { 
      duration: 0.3,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

// Utility Components
const ProfileAvatar = ({ src, size = "md", className = "" }) => {
  const [error, setError] = useState(false);
  
  const sizes = {
    sm: "w-16 h-16",
    md: "w-32 h-32 md:w-40 md:h-40",
    lg: "w-40 h-40 md:w-52 md:h-52"
  };

  const getProfileImageUrl = (imageUrl) => {
    if (!imageUrl) return '/default-profile.png';
    
    const cleanUrl = imageUrl
      .replace('https://backend-production-1786.up.railway.app/profiles/', '')
      .replace('/profiles/', '')
      .replace('profiles/', '')
      .replace(/^\//, '');
    
    return `${CLOUDFLARE_PROFILES_URL}/${cleanUrl}`;
  };

  const imageUrl = error ? '/default-profile.png' : getProfileImageUrl(src);

  return (
    <motion.div 
      className={`relative ${sizes[size]} ${className}`}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6, type: "spring", damping: 15 }}
      whileHover={{ scale: 1.08 }}
    >
      <motion.div 
        className="absolute inset-0 rounded-full bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-600 p-1"
        whileHover={{
          rotate: 360,
          transition: { duration: 1, ease: "easeInOut" }
        }}
      >
        <div className="w-full h-full rounded-full bg-white dark:bg-gray-800 p-1">
          <img 
            src={imageUrl}
            alt="Profile" 
            className="w-full h-full rounded-full object-cover"
            onError={() => setError(true)}
          />
        </div>
      </motion.div>
      
      <motion.div 
        className="absolute bottom-1 right-1 md:bottom-2 md:right-2 w-3 h-3 md:w-4 md:h-4 bg-green-500 rounded-full border-2 border-white dark:border-gray-800 shadow-sm"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.5, type: "spring" }}
      />
    </motion.div>
  );
};

const ActionButton = ({ 
  children, 
  onClick, 
  variant = "primary", 
  size = "md", 
  icon: Icon,
  className = "",
  ...props 
}) => {
  const variants = {
    primary: "bg-gradient-to-r from-amber-600 to-yellow-600 text-white shadow-lg hover:shadow-xl dark:shadow-xl dark:hover:shadow-2xl",
    secondary: "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600",
    ghost: "bg-white/10 dark:bg-white/5 backdrop-blur-sm text-white border border-white/20 dark:border-white/10 hover:bg-white/20 dark:hover:bg-white/10"
  };
  
  const sizes = {
    sm: "px-3 py-1.5 text-sm",
    md: "px-4 py-2 text-sm md:px-6 md:py-3 md:text-base",
    lg: "px-6 py-3 text-base md:px-8 md:py-4 md:text-lg"
  };

  return (
    <motion.button
      className={`
        ${variants[variant]} 
        ${sizes[size]} 
        rounded-xl font-medium transition-all duration-300 
        flex items-center justify-center gap-2 
        focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800
        ${className}
      `}
      onClick={onClick}
      whileHover={{ scale: 1.05, y: -2 }}
      whileTap={{ scale: 0.98 }}
      {...props}
    >
      {Icon && <Icon className="w-4 h-4 md:w-5 md:h-5" />}
      {children}
    </motion.button>
  );
};

const SocialLink = ({ href, icon: Icon, platform, color, variant = "banner" }) => {
  const baseClasses = "p-2 md:p-3 rounded-lg md:rounded-xl text-white transition-all duration-300";
  const bannerClasses = "backdrop-blur-sm border border-white/20 dark:border-white/10";
  const mobileClasses = "shadow-lg dark:shadow-xl";
  
  return (
    <motion.a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`
        ${baseClasses}
        ${variant === "banner" ? bannerClasses : mobileClasses}
        ${color} hover:scale-110 hover:rotate-3
      `}
      whileHover={{ scale: 1.1, rotate: 3 }}
      whileTap={{ scale: 0.95 }}
      title={platform}
    >
      <Icon className="w-4 h-4 md:w-5 md:h-5" />
    </motion.a>
  );
};

const StatCard = ({ icon: Icon, value, label, color = "amber" }) => {
  const colorClasses = {
    amber: "from-amber-500 to-yellow-600",
    yellow: "from-yellow-400 to-amber-500",
    orange: "from-amber-600 to-orange-600",
    gold: "from-yellow-500 to-amber-600"
  };

  return (
    <motion.div
      className="bg-white dark:bg-gray-800 rounded-xl md:rounded-2xl p-3 md:p-6 shadow-lg dark:shadow-black/50 border border-amber-100 dark:border-gray-700 overflow-hidden relative"
      variants={cardHover}
      initial="rest"
      whileHover="hover"
    >
      <div className={`absolute top-0 right-0 w-12 h-12 md:w-20 md:h-20 bg-gradient-to-br ${colorClasses[color]} opacity-10 rounded-full -translate-y-3 translate-x-3 md:-translate-y-6 md:translate-x-6`} />
      
      <div className="relative flex items-center gap-2 md:gap-4">
        <div className={`p-2 md:p-3 rounded-lg md:rounded-xl bg-gradient-to-br ${colorClasses[color]} shadow-lg flex-shrink-0`}>
          <Icon className="w-4 h-4 md:w-6 md:h-6 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-lg md:text-2xl lg:text-3xl font-bold text-gray-800 dark:text-gray-200 leading-tight">{value}</div>
          <div className="text-xs md:text-sm lg:text-base text-gray-600 dark:text-gray-400 leading-tight">{label}</div>
        </div>
      </div>
    </motion.div>
  );
};

const PropertySlider = ({ 
  properties, 
  title, 
  icon: Icon, 
  onFavoriteUpdate, 
  slidesPerView = 2, 
  viewAllLink, 
  usePropertyCard = false 
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(slidesPerView);
  const navigate = useNavigate();
  
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setItemsPerView(1);
      } else if (window.innerWidth < 1024) {
        setItemsPerView(2);
      } else {
        setItemsPerView(slidesPerView);
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [slidesPerView]);
  
  const maxSlides = Math.max(0, Math.ceil(properties.length / itemsPerView) - 1);

  if (properties.length === 0) {
    return (
      <motion.div 
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl dark:shadow-black/50 p-8 border border-amber-100 dark:border-gray-700"
        variants={fadeInUp}
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-gradient-to-br from-amber-600 to-yellow-600 rounded-xl">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-xl md:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-amber-700 to-yellow-800 dark:from-amber-400 dark:to-yellow-500">{title}</h2>
        </div>
        
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 bg-amber-50 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
            <Icon className="w-8 h-8 text-amber-500 dark:text-amber-400" />
          </div>
          <p className="text-gray-500 dark:text-gray-400">No hay contenido disponible</p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div 
      className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl dark:shadow-black/50 p-6 md:p-8 border border-amber-100 dark:border-gray-700"
      variants={fadeInUp}
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-amber-600 to-yellow-600 rounded-xl">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-xl md:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-amber-700 to-yellow-800 dark:from-amber-400 dark:to-yellow-500">{title}</h2>
        </div>
        
        {viewAllLink && (
          <ActionButton
            variant="primary"
            size="sm"
            onClick={() => navigate(viewAllLink)}
            className="hidden sm:flex"
          >
            Ver todos
          </ActionButton>
        )}
      </div>

      <div className="relative">
        <motion.button
          onClick={() => setCurrentSlide(prev => Math.max(0, prev - 1))}
          disabled={currentSlide === 0}
          className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full shadow-lg dark:shadow-black/50 transition-all duration-300 ${
            currentSlide === 0 
              ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed' 
              : 'bg-white dark:bg-gray-800 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-gray-700 hover:shadow-xl dark:hover:shadow-black/70'
          } hidden md:flex items-center justify-center border border-gray-200 dark:border-gray-600`}
          style={{ transform: 'translateX(-50%) translateY(-50%)' }}
          whileHover={currentSlide !== 0 ? { scale: 1.1 } : {}}
          whileTap={currentSlide !== 0 ? { scale: 0.95 } : {}}
        >
          <ChevronLeft className="w-5 h-5" />
        </motion.button>
        
        <motion.button
          onClick={() => setCurrentSlide(prev => Math.min(maxSlides, prev + 1))}
          disabled={currentSlide >= maxSlides}
          className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full shadow-lg dark:shadow-black/50 transition-all duration-300 ${
            currentSlide >= maxSlides 
              ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed' 
              : 'bg-white dark:bg-gray-800 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-gray-700 hover:shadow-xl dark:hover:shadow-black/70'
          } hidden md:flex items-center justify-center border border-gray-200 dark:border-gray-600`}
          style={{ transform: 'translateX(50%) translateY(-50%)' }}
          whileHover={currentSlide < maxSlides ? { scale: 1.1 } : {}}
          whileTap={currentSlide < maxSlides ? { scale: 0.95 } : {}}
        >
          <ChevronRight className="w-5 h-5" />
        </motion.button>

        <div className="overflow-hidden mx-4 md:mx-8">
          <motion.div 
            className="flex transition-transform duration-500 ease-out"
            style={{ transform: `translateX(-${currentSlide * (100 / itemsPerView)}%)` }}
          >
            {properties.map((property) => (
              <motion.div 
                key={property.id} 
                className="flex-shrink-0 px-2"
                style={{ width: `${100 / itemsPerView}%` }}
                variants={cardHover}
                initial="rest"
                whileHover="hover"
              >
                {usePropertyCard ? (
                  <PropertyCard 
                    property={property}
                    onFavoriteUpdate={onFavoriteUpdate}
                  />
                ) : (
                  <PropertyCardPro 
                    item={property}
                    onFavoriteUpdate={onFavoriteUpdate}
                    isProject={property.tipo_proyecto !== undefined}
                  />
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className="flex items-center justify-center gap-4 mt-6 md:hidden">
        <motion.button
          onClick={() => setCurrentSlide(prev => Math.max(0, prev - 1))}
          disabled={currentSlide === 0}
          className={`p-2 rounded-lg transition-colors ${
            currentSlide === 0 
              ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500' 
              : 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-900/50'
          }`}
          whileHover={currentSlide !== 0 ? { scale: 1.1 } : {}}
          whileTap={currentSlide !== 0 ? { scale: 0.95 } : {}}
        >
          <ChevronLeft className="w-5 h-5" />
        </motion.button>
        
        <div className="flex gap-2">
          {Array.from({ length: maxSlides + 1 }).map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-2 h-2 rounded-full transition-colors ${
                index === currentSlide ? 'bg-amber-600 dark:bg-amber-400' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            />
          ))}
        </div>
        
        <motion.button
          onClick={() => setCurrentSlide(prev => Math.min(maxSlides, prev + 1))}
          disabled={currentSlide >= maxSlides}
          className={`p-2 rounded-lg transition-colors ${
            currentSlide >= maxSlides 
              ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500' 
              : 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-900/50'
          }`}
          whileHover={currentSlide < maxSlides ? { scale: 1.1 } : {}}
          whileTap={currentSlide < maxSlides ? { scale: 0.95 } : {}}
        >
          <ChevronRight className="w-5 h-5" />
        </motion.button>
      </div>

      {viewAllLink && (
        <ActionButton
          variant="primary"
          size="md"
          onClick={() => navigate(viewAllLink)}
          className="w-full mt-4 sm:hidden"
        >
          Ver todos
        </ActionButton>
      )}
    </motion.div>
  );
};

const ReviewCard = ({ review }) => {
  const [error, setError] = useState(false);

  const getProfileImageUrl = (imageUrl) => {
    if (!imageUrl) return '/default-avatar.png';
    
    const cleanUrl = imageUrl
      .replace('https://backend-production-1786.up.railway.app/profiles/', '')
      .replace('/profiles/', '')
      .replace('profiles/', '')
      .replace(/^\//, '');
    
    return `${CLOUDFLARE_PROFILES_URL}/${cleanUrl}`;
  };

  const imageUrl = error ? '/default-avatar.png' : getProfileImageUrl(review.usuario_imagen);
  const formattedDate = new Date(review.fecha).toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <motion.div 
      className="bg-gradient-to-br from-white to-amber-50/30 dark:from-gray-800 dark:to-gray-900 rounded-2xl p-6 shadow-lg dark:shadow-black/50 border border-amber-100/50 dark:border-gray-700 h-full"
      variants={cardHover}
      initial="rest"
      whileHover="hover"
    >
      <div className="flex items-center gap-4 mb-4">
        <img 
          src={imageUrl}
          alt={review.usuario_nombre || 'Usuario'}
          className="w-12 h-12 rounded-full object-cover border-2 border-amber-200 dark:border-amber-700"
          onError={() => setError(true)}
        />
        <div className="flex-1">
          <h3 className="font-semibold text-gray-800 dark:text-gray-200">{review.usuario_nombre}</h3>
          <div className="flex items-center gap-1">
            {[...Array(5)].map((_, index) => (
              <Star 
                key={index}
                className={`w-4 h-4 ${
                  index < review.rating 
                    ? 'fill-amber-400 text-amber-400' 
                    : 'text-gray-300 dark:text-gray-600'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
      
      <p className="text-gray-600 dark:text-gray-400 mb-4 leading-relaxed line-clamp-4">
        {review.comentario}
      </p>
      
      <div className="flex items-center justify-between mt-auto pt-4 border-t border-amber-100 dark:border-gray-700">
        <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          {formattedDate}
        </p>
        <div className="flex gap-2">
          <motion.button 
            className="p-1 text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 transition-colors"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <Heart className="w-4 h-4" />
          </motion.button>
          <motion.button 
            className="p-1 text-gray-400 dark:text-gray-500 hover:text-amber-500 dark:hover:text-amber-400 transition-colors"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <ThumbsUp className="w-4 h-4" />
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

const TabButton = ({ active, onClick, children, icon: Icon }) => (
  <motion.button
    onClick={onClick}
    className={`
      px-4 py-2 md:px-6 md:py-3 rounded-xl font-medium transition-all duration-300 
      flex items-center gap-2 whitespace-nowrap
      ${active 
        ? 'bg-gradient-to-r from-amber-600 to-yellow-600 text-white shadow-lg dark:shadow-xl' 
        : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-amber-50 dark:hover:bg-gray-700 border border-amber-200 dark:border-gray-600'}
    `}
    whileHover={{ scale: active ? 1 : 1.02 }}
    whileTap={{ scale: 0.98 }}
  >
    {Icon && <Icon className="w-4 h-4" />}
    {children}
  </motion.button>
);

const ServiceCard = ({ title, description, icon: Icon }) => (
  <motion.div 
    className="bg-gradient-to-br from-white to-amber-50/30 dark:from-gray-800 dark:to-gray-900 rounded-2xl p-6 shadow-lg dark:shadow-black/50 border border-amber-100/50 dark:border-gray-700 h-full"
    variants={cardHover}
    initial="rest"
    whileHover="hover"
  >
    <div className="flex items-center gap-4 mb-4">
      <div className="p-3 rounded-xl bg-gradient-to-br from-amber-600 to-yellow-600 shadow-lg">
        <Icon className="w-6 h-6 text-white" />
      </div>
      <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">{title}</h3>
    </div>
    {description && <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{description}</p>}
  </motion.div>
);

const BusinessHours = ({ horarios }) => {
  const daysOfWeek = {
    lunes_viernes: "Lunes a Viernes",
    sabado: "Sábado",
    domingo: "Domingo"
  };

  const isNewFormat = horarios && Object.values(horarios).some(h => h && typeof h === 'object' && 'open' in h);

  return (
    <motion.div 
      className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg dark:shadow-black/50 border border-amber-100 dark:border-gray-700 h-full"
      variants={cardHover}
      initial="rest"
      whileHover="hover"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-gradient-to-br from-amber-600 to-yellow-600 rounded-xl">
          <Clock className="w-5 h-5 text-white" />
        </div>
        <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200">Horario de Atención</h3>
      </div>
      <div className="space-y-4">
        {['lunes_viernes', 'sabado', 'domingo'].map(day => {
          if (!horarios || !horarios[day]) return null;
          
          const hours = horarios[day];
          const inicio = isNewFormat ? hours.open : hours.inicio;
          const fin = isNewFormat ? hours.close : hours.fin;
          const isOpen = isNewFormat ? hours.isOpen : (inicio !== '00:00' || fin !== '00:00');

          return (
            <div key={day} className="flex justify-between items-center p-3 rounded-lg hover:bg-amber-50/50 dark:hover:bg-gray-700/50 transition-colors">
              <span className="font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-amber-500 dark:text-amber-400" />
                {daysOfWeek[day]}
              </span>
              <span className={`text-gray-600 dark:text-gray-400 px-3 py-1 rounded-full text-sm ${
                !isOpen || (inicio === '00:00' && fin === '00:00')
                  ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                  : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
              }`}>
                {!isOpen || (inicio === '00:00' && fin === '00:00')
                  ? 'Cerrado'
                  : `${inicio} - ${fin}`
                }
              </span>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
};

// Function to get the corresponding icon
const getServiceIcon = (serviceName) => {
  switch (serviceName.toLowerCase()) {
    case 'construcción residencial':
      return Home;
    case 'construcción comercial':
    case 'proyectos comerciales':
    case 'proyectos industriales':
      return Building2;
    case 'remodelaciones':
      return PenToolIcon;
    case 'consultoría':
    case 'consultoría técnica':
    case 'gestión de proyectos':
      return Hammer;
    case 'diseño arquitectónico':
      return PenToolIcon;
    default:
      return Building2;
  }
};

// Default services
const defaultServices = [
  {
    title: "Construcción Residencial",
    description: "Desarrollo de proyectos residenciales con los más altos estándares de calidad, utilizando materiales premium y técnicas de construcción avanzadas.",
    icon: Home
  },
  {
    title: "Proyectos Comerciales",
    description: "Espacios comerciales modernos diseñados para maximizar la funcionalidad y atraer clientes, con soluciones innovadoras para cada tipo de negocio.",
    icon: Building2
  },
  {
    title: "Remodelaciones",
    description: "Renovación profesional de espacios existentes, transformando su hogar o negocio con un enfoque que respeta la estructura original mientras añade valor.",
    icon: PenToolIcon
  },
  {
    title: "Consultoría",
    description: "Asesoría especializada en todas las fases de su proyecto, desde la planificación inicial hasta la finalización, garantizando resultados óptimos.",
    icon: Hammer
  }
];

// Utility functions
const getPrecioDesdeProyecto = (proyecto) => {
  const precios = [];
  
  if (proyecto.tiene_1dorm && proyecto.precio_1dorm_soles > 0) {
    precios.push(proyecto.precio_1dorm_soles);
  }
  
  if (proyecto.tiene_2dorm && proyecto.precio_2dorm_soles > 0) {
    precios.push(proyecto.precio_2dorm_soles);
  }
  
  if (proyecto.tiene_3dorm && proyecto.precio_3dorm_soles > 0) {
    precios.push(proyecto.precio_3dorm_soles);
  }
  
  if (proyecto.tiene_4dorm && proyecto.precio_4dorm_soles > 0) {
    precios.push(proyecto.precio_4dorm_soles);
  }
  
  return precios.length > 0 ? Math.min(...precios) : 0;
};

const formatProject = (project) => {
  return {
    ...project,
    precio_desde: getPrecioDesdeProyecto(project),
    tipo_precio: 'Desde (S/.)',
    imagenes: Array.isArray(project.imagenes) 
      ? project.imagenes 
      : (project.imagenes ? project.imagenes.split(',') : []),
    bancos_financiamiento: typeof project.bancos_financiamiento === 'string' 
      ? JSON.parse(project.bancos_financiamiento) 
      : (project.bancos_financiamiento || []),
    amenidades: typeof project.amenidades === 'string' 
      ? JSON.parse(project.amenidades) 
      : (project.amenidades || []),
    vendedor_imagen: project.vendedor_foto_perfil 
      ? `/profiles/${project.vendedor_foto_perfil}` 
      : null
  };
};

// Main Component
const ConsProfile = ({ username }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const isOwnProfile = location.pathname === '/mi_perfil';

  // Main states
  const [userData, setUserData] = useState(null);
  const [properties, setProperties] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [projects, setProjects] = useState([]);
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [recentProperties, setRecentProperties] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [existingReview, setExistingReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // UI states
  const [isRatingModalOpen, setIsRatingModalOpen] = useState(false);
  const [reviewFilter, setReviewFilter] = useState('highest');
  const [activeTab, setActiveTab] = useState('resumen');

  // Data fetching
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        const profileResponse = await fetch(
          `https://backend-production-1786.up.railway.app/api/vendedores/constructora/${username}`,
          {
            headers: {
              'Authorization': token ? `Bearer ${token}` : '',
              'Accept': 'application/json'
            }
          }
        );
    
        if (!profileResponse.ok) {
          const errorData = await profileResponse.json();
          throw new Error(errorData.message || 'Error al cargar el perfil de la constructora');
        }
    
        const profileData = await profileResponse.json();
        setUserData(profileData);
        setReviews(profileData.reviews || []);
        
        // Fetch properties
        const propertiesResponse = await fetch(
          `https://backend-production-1786.up.railway.app/api/propiedades/vendedor/${username}/public`,
          {
            headers: {
              'Authorization': token ? `Bearer ${token}` : '',
              'Accept': 'application/json'
            }
          }
        );
        
        let propertiesData = [];
        if (propertiesResponse.ok) {
          propertiesData = await propertiesResponse.json();
          setProperties(propertiesData);
        }
        
        // Fetch rentals
        const rentalsResponse = await fetch(
          `https://backend-production-1786.up.railway.app/api/alquileres/vendedor/${username}/public`,
          {
            headers: {
              'Authorization': token ? `Bearer ${token}` : '',
              'Accept': 'application/json'
            }
          }
        );
        
        let rentalsData = [];
        if (rentalsResponse.ok) {
          rentalsData = await rentalsResponse.json();
          setRentals(rentalsData);
        }
        
        // Fetch projects
        const projectsResponse = await fetch(
          `https://backend-production-1786.up.railway.app/api/proyectos/vendedor/${username}/public`,
          {
            headers: {
              'Authorization': token ? `Bearer ${token}` : '',
              'Accept': 'application/json'
            }
          }
        );
        
        let projectsData = [];
        if (projectsResponse.ok) {
          const rawProjectsData = await projectsResponse.json();
          projectsData = rawProjectsData.map(formatProject);
          setProjects(projectsData);
        }
    
        // Featured projects (up to 8)
        const featuredProjectsData = projectsData
          .filter(proj => proj.destacada)
          .sort((a, b) => new Date(b.fecha_publicacion) - new Date(a.fecha_publicacion))
          .slice(0, 8);

        if (featuredProjectsData.length === 0 && projectsData.length > 0) {
          featuredProjectsData.push(...projectsData.slice(0, 8));
        }
        
        setFeaturedProjects(featuredProjectsData);
        
        // Featured properties and rentals combined (up to 9)
        const allProperties = [...propertiesData, ...rentalsData];
        const featured = allProperties
          .filter(prop => prop.destacada)
          .sort((a, b) => new Date(b.fecha_publicacion) - new Date(a.fecha_publicacion))
          .slice(0, 9);

        if (featured.length === 0 && allProperties.length > 0) {
          featured.push(...allProperties.slice(0, 9));
        }
        
        setFeaturedProperties(featured);
    
        // Recent properties and rentals combined (up to 6)
        const recent = [...allProperties]
          .sort((a, b) => new Date(b.fecha_publicacion) - new Date(a.fecha_publicacion))
          .slice(0, 6);
        
        setRecentProperties(recent);
    
        if (user && token) {
          try {
            const existingReviewResponse = await fetch(
              `https://backend-production-1786.up.railway.app/api/reviews/constructora/${profileData.id}/user`,
              {
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              }
            );
    
            if (existingReviewResponse.ok) {
              const existingReviewData = await existingReviewResponse.json();
              setExistingReview(existingReviewData);
            }
          } catch (reviewError) {
            console.warn('Error fetching existing review:', reviewError);
          }
        }
    
      } catch (error) {
        console.error('Error al cargar datos:', error);
        setError(error.message || 'Error al cargar el perfil');
      } finally {
        setLoading(false);
      }
    };
  
    fetchData();
  }, [username, user]);

  // Event handlers
  const handleRateClick = () => {
    if (!user) {
      toast({
        title: "Inicio de sesión requerido",
        description: "Inicia sesión para empezar a calificar a este y a otros socios.",
        variant: "warning",
      });
      return;
    }
    setIsRatingModalOpen(true);
  };

  const handleRatingSubmit = async (ratingData) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`https://backend-production-1786.up.railway.app/api/reviews/constructora/${userData.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          rating: ratingData.rating,
          comment: ratingData.comment
        })
      });
  
      const data = await response.json();
      
      setIsRatingModalOpen(false);
      setReviews(prevReviews => [data, ...prevReviews]);
  
      toast({
        title: "¡Gracias por tu calificación!",
        description: "Tu opinión ha sido guardada exitosamente.",
        variant: "success",
      });
  
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo guardar la calificación. Por favor, intenta de nuevo.",
        variant: "destructive",
      });
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Perfil de ${userData?.nombre}`,
          text: userData?.slogan || "Construyendo el Futuro de tu Hogar",
          url: window.location.href
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      try {
        await navigator.clipboard.writeText(window.location.href);
        toast({
          title: "¡Enlace copiado!",
          description: "El enlace del perfil ha sido copiado al portapapeles.",
          variant: "success",
        });
      } catch (error) {
        console.error('Error copying to clipboard:', error);
      }
    }
  };

  const getBannerImageUrl = (imageUrl) => {
    if (!imageUrl) return 'default-banner.png';
    
    if (imageUrl.includes('r2.dev')) {
      return imageUrl;
    }
    
    const cleanUrl = imageUrl
      .replace('https://backend-production-1786.up.railway.app/', '')
      .replace('/banners/', '')
      .replace('banners/', '')
      .replace(/^\//, '');
    
    return `${CLOUDFLARE_BANNERS_URL}/${cleanUrl}`;
  };

  const totalProperties = properties.length + rentals.length + projects.length;

  const stats = [
    {
      label: "Proyectos",
      value: totalProperties,
      icon: Building2,
      color: "amber"
    },
    {
      label: "Experiencia",
      value: userData?.anos_experiencia ? `${userData.anos_experiencia} años` : "N/A",
      icon: Trophy,
      color: "yellow"
    },
    {
      label: "Reseñas",
      value: reviews.length,
      icon: Star,
      color: "orange"
    },
    {
      label: "Reconocimiento",
      value: "Premium",
      icon: Award,
      color: "gold"
    }
  ];

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-6 bg-gradient-to-br from-amber-50 via-white to-yellow-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <motion.div
          className="relative"
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        >
          <div className="w-24 h-24 border-4 border-amber-200 dark:border-amber-800 rounded-full">
            <div className="absolute top-0 left-0 w-24 h-24 border-4 border-amber-600 dark:border-amber-400 rounded-full border-t-transparent animate-spin"></div>
          </div>
        </motion.div>
        <motion.div
          className="text-center"
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          <p className="text-xl font-semibold text-amber-700 dark:text-amber-300 mb-2">Cargando perfil...</p>
          <p className="text-gray-600 dark:text-gray-400">Obteniendo información de la constructora</p>
        </motion.div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-red-50 to-white dark:from-gray-900 dark:to-gray-800 px-4">
        <motion.div 
          className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl dark:shadow-black/50 p-8 max-w-md w-full text-center border border-gray-100 dark:border-gray-700"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="bg-red-100 dark:bg-red-900/30 w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center">
            <AlertCircle className="w-10 h-10 text-red-500 dark:text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-4">
            Error al cargar el perfil
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
          <ActionButton
            variant="primary"
            onClick={() => navigate(-1)}
            className="w-full"
          >
            Volver atrás
          </ActionButton>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* Hero Banner */}
      <div className="relative h-80 md:h-80 lg:h-96 overflow-hidden">
        <motion.div 
          className="absolute inset-0"
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
        >
          <img 
            src={getBannerImageUrl(userData?.banner_image)}
            alt="Banner"
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = '/default-banner.jpg';
            }}
          />
          
          <motion.div 
            className="absolute inset-0 bg-gradient-to-r from-amber-900/90 via-amber-800/70 to-yellow-800/80"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
          >
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-2 h-2 bg-white rounded-full opacity-30"
                style={{
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                }}
                animate={{
                  y: [0, -20, 0],
                  opacity: [0.3, 0.8, 0.3],
                }}
                transition={{
                  duration: 3 + Math.random() * 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: Math.random() * 2,
                }}
              />
            ))}
          </motion.div>
        </motion.div>

        {/* Profile Content */}
        <div className="absolute inset-0 flex items-center justify-center p-6 md:p-4">
          <div className="max-w-7xl mx-auto w-full">
            {/* Mobile Layout */}
            <div className="flex flex-col items-center text-center md:hidden">
              <ProfileAvatar 
                src={userData?.foto_perfil ? `https://backend-production-1786.up.railway.app${userData.foto_perfil}` : null}
                size="md"
                className="mb-4 mt-10"
              />
              
              <motion.h1 
                className="text-2xl font-bold text-white mb-2"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                {userData?.nombre}
              </motion.h1>
              
              <motion.div 
                className="flex items-center gap-2 mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <span className="px-3 py-1 bg-white/20 dark:bg-white/10 backdrop-blur-sm text-white rounded-full text-sm font-medium border border-white/30 dark:border-white/20">
                  🏗️ Constructora
                </span>
              </motion.div>
              
              <motion.div 
                className="flex items-center text-white/90 text-sm mb-16"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <MapPin className="w-4 h-4 mr-1" />
                {userData?.ubicacion || 'Sin ubicación'}
              </motion.div>

              <motion.div 
                className="flex flex-wrap gap-2 md:gap-3 justify-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                {isOwnProfile ? (
                  <ActionButton
                    variant="ghost"
                    size="sm"
                    icon={Edit2}
                    onClick={() => navigate('/edit-profile')}
                  >
                    Editar
                  </ActionButton>
                ) : (
                  <ActionButton
                    variant="ghost"
                    size="sm"
                    icon={Star}
                    onClick={handleRateClick}
                  >
                    Calificar
                  </ActionButton>
                )}
                
                <motion.button
                  onClick={handleShare}
                  className="p-2 md:p-3 rounded-lg md:rounded-xl bg-white/20 dark:bg-white/10 backdrop-blur-sm text-white border border-white/20 dark:border-white/10 hover:bg-white/30 dark:hover:bg-white/20 transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Share2 className="w-4 h-4 md:w-5 md:h-5" />
                </motion.button>
              </motion.div>
            </div>

            {/* Desktop Layout */}
            <div className="hidden md:flex items-end gap-8">
              <ProfileAvatar 
                src={userData?.foto_perfil ? `https://backend-production-1786.up.railway.app${userData.foto_perfil}` : null}
                size="lg"
              />
              
              <div className="flex-1">
                <motion.h1 
                  className="text-4xl lg:text-5xl font-bold text-white mb-3"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  {userData?.nombre}
                </motion.h1>
                
                <motion.div 
                  className="flex items-center gap-4 mb-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <span className="px-4 py-2 bg-white/20 dark:bg-white/10 backdrop-blur-sm text-white rounded-full font-medium border border-white/30 dark:border-white/20">
                    🏗️ Constructora
                  </span>
                  <div className="flex items-center text-white/90">
                    <MapPin className="w-5 h-5 mr-2" />
                    {userData?.ubicacion || 'Sin ubicación'}
                  </div>
                </motion.div>
              </div>
              
              <motion.div 
                className="flex gap-3"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                {isOwnProfile ? (
                  <ActionButton
                    variant="ghost"
                    size="md"
                    icon={Edit2}
                    onClick={() => navigate('/edit-profile')}
                  >
                    Editar Perfil
                  </ActionButton>
                ) : (
                  <ActionButton
                    variant="ghost"
                    size="md"
                    icon={Star}
                    onClick={handleRateClick}
                  >
                    Calificar
                  </ActionButton>
                )}
                
                <div className="flex gap-2">
                  {userData?.email && (
                    <SocialLink 
                      href={`mailto:${userData.email}`}
                      icon={Mail}
                      platform="Email"
                      color="bg-amber-500/70 hover:bg-amber-500"
                      variant="banner"
                    />
                  )}
                  {userData?.telefono && (
                    <SocialLink 
                      href={`tel:${userData.telefono}`}
                      icon={Phone}
                      platform="Teléfono"
                      color="bg-amber-500/70 hover:bg-amber-500"
                      variant="banner"
                    />
                  )}
                  {userData?.facebook && (
                    <SocialLink 
                      href={`https://facebook.com/${userData.facebook}`}
                      icon={Facebook}
                      platform="Facebook"
                      color="bg-amber-600/70 hover:bg-amber-600"
                      variant="banner"
                    />
                  )}
                  {userData?.instagram && (
                    <SocialLink 
                      href={`https://instagram.com/${userData.instagram}`}
                      icon={Instagram}
                      platform="Instagram"
                      color="bg-amber-600/70 hover:bg-amber-600"
                      variant="banner"
                    />
                  )}
                  
                  <motion.button
                    onClick={handleShare}
                    className="p-2 md:p-3 rounded-lg md:rounded-xl bg-white/20 dark:bg-white/10 backdrop-blur-sm text-white border border-white/20 dark:border-white/10 hover:bg-white/30 dark:hover:bg-white/20 transition-all duration-300"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Share2 className="w-4 h-4 md:w-5 md:h-5" />
                  </motion.button>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Slogan Bar */}
        <motion.div 
          className="absolute bottom-0 left-0 right-0 bg-black/60 backdrop-blur-sm border-t border-white/10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7 }}
        >
          <div className="max-w-7xl mx-auto px-4 py-4">
            <p className="text-white text-center font-medium text-lg">
              {userData?.slogan || "Construyendo el Futuro de tu Hogar"}
            </p>
          </div>
        </motion.div>
      </div>

      {/* Mobile Social Links & Actions */}
      <motion.div 
        className="md:hidden bg-white dark:bg-gray-800 shadow-lg dark:shadow-black/50 border-b border-gray-100 dark:border-gray-700 py-4"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-center gap-2 flex-wrap">
            {isOwnProfile ? (
              <ActionButton
                variant="secondary"
                size="sm"
                icon={Edit2}
                onClick={() => navigate('/edit-profile')}
                className="flex-shrink-0"
              >
                Editar
              </ActionButton>
            ) : (
              <ActionButton
                variant="secondary"
                size="sm"
                icon={Star}
                onClick={handleRateClick}
                className="flex-shrink-0"
              >
                Calificar
              </ActionButton>
            )}
            
            <motion.button
              onClick={handleShare}
              className="px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-300 flex items-center gap-2 text-sm flex-shrink-0"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Share2 className="w-4 h-4" />
              Compartir
            </motion.button>
            
            {userData?.email && (
              <SocialLink 
                href={`mailto:${userData.email}`}
                icon={Mail}
                platform="Email"
                color="bg-amber-500 hover:bg-amber-600"
                variant="mobile"
              />
            )}
            {userData?.telefono && (
              <SocialLink 
                href={`tel:${userData.telefono}`}
                icon={Phone}
                platform="Teléfono"
                color="bg-amber-500 hover:bg-amber-600"
                variant="mobile"
              />
            )}
            {userData?.facebook && (
              <SocialLink 
                href={`https://facebook.com/${userData.facebook}`}
                icon={Facebook}
                platform="Facebook"
                color="bg-amber-600 hover:bg-amber-700"
                variant="mobile"
              />
            )}
            {userData?.instagram && (
              <SocialLink 
                href={`https://instagram.com/${userData.instagram}`}
                icon={Instagram}
                platform="Instagram"
                color="bg-amber-600 hover:bg-amber-700"
                variant="mobile"
              />
            )}
          </div>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8 md:py-12">
        {/* Stats Grid */}
        <motion.div 
          className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-8 md:mb-12"
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.8 }}
        >
          {stats.map((stat, index) => (
            <motion.div key={index} variants={fadeInUp}>
              <StatCard {...stat} />
            </motion.div>
          ))}
        </motion.div>

        {/* About Section */}
        <motion.div 
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl dark:shadow-black/50 p-6 md:p-8 mb-8 md:mb-12 border border-amber-100 dark:border-gray-700"
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-gradient-to-br from-amber-600 to-yellow-600 rounded-xl">
              <Trophy className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl md:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-amber-700 to-yellow-800 dark:from-amber-400 dark:to-yellow-500">
              Sobre Nosotros
            </h2>
          </div>
          
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed text-sm md:text-base">
            {userData?.sobre_nosotros || 
            'Somos una empresa constructora líder comprometida con la excelencia y la innovación en cada proyecto. Nuestra misión es transformar ideas en estructuras sólidas que perduren en el tiempo, siempre poniendo al cliente en el centro de todo lo que hacemos.'}
          </p>
        </motion.div>

        {/* Navigation Tabs */}
        <div className="flex gap-2 md:gap-3 mb-8 overflow-x-auto pb-2 scrollbar-hide">
          <TabButton 
            active={activeTab === 'resumen'} 
            onClick={() => setActiveTab('resumen')}
            icon={Home}
          >
            Resumen
          </TabButton>
          <TabButton 
            active={activeTab === 'proyectos'} 
            onClick={() => setActiveTab('proyectos')}
            icon={Castle}
          >
            Proyectos ({projects.length})
          </TabButton>
          <TabButton 
            active={activeTab === 'propiedades'} 
            onClick={() => setActiveTab('propiedades')}
            icon={Building2}
          >
            Propiedades
          </TabButton>
          <TabButton 
            active={activeTab === 'reseñas'} 
            onClick={() => setActiveTab('reseñas')}
            icon={Star}
          >
            Reseñas ({reviews.length})
          </TabButton>
          <TabButton 
            active={activeTab === 'servicios'} 
            onClick={() => setActiveTab('servicios')}
            icon={Hammer}
          >
            Servicios
          </TabButton>
          <TabButton 
            active={activeTab === 'ubicación'} 
            onClick={() => setActiveTab('ubicación')}
            icon={MapPin}
          >
            Ubicación
          </TabButton>
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'resumen' && (
            <motion.div
              key="resumen"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-8"
            >
              {/* Business Hours and Services Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="space-y-8">
                  <BusinessHours 
                    horarios={userData?.horario_atencion || {
                      lunes_viernes: { inicio: '09:00', fin: '18:00' },
                      sabado: { inicio: '09:00', fin: '13:00' },
                      domingo: { inicio: '00:00', fin: '00:00' }
                    }}
                  />
                </div>

                <div className="md:col-span-2">
                  <motion.div 
                    className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl dark:shadow-black/50 p-8 border border-amber-100 dark:border-gray-700"
                    variants={fadeInUp}
                  >
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-2 bg-gradient-to-br from-amber-600 to-yellow-600 rounded-xl">
                        <Hammer className="w-5 h-5 text-white" />
                      </div>
                      <h2 className="text-xl md:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-amber-700 to-yellow-800 dark:from-amber-400 dark:to-yellow-500">Nuestros Servicios</h2>
                    </div>
                    <motion.div 
                      className="grid grid-cols-1 md:grid-cols-2 gap-5"
                      variants={staggerContainer}
                      initial="hidden"
                      animate="visible"
                    >
                      {Array.isArray(userData?.servicios) && userData.servicios.length > 0 ? (
                        userData.servicios.map((servicio) => (
                          <motion.div key={servicio} variants={fadeInUp}>
                            <ServiceCard 
                              title={servicio}
                              icon={getServiceIcon(servicio)}
                              description={`Servicios profesionales de ${servicio.toLowerCase()} con los más altos estándares de calidad.`}
                            />
                          </motion.div>
                        ))
                      ) : (
                        defaultServices.map((service) => (
                          <motion.div key={service.title} variants={fadeInUp}>
                            <ServiceCard 
                              title={service.title}
                              description={service.description}
                              icon={service.icon}
                            />
                          </motion.div>
                        ))
                      )}
                    </motion.div>
                  </motion.div>
                </div>
              </div>
              
              {/* Featured Properties */}
              {featuredProperties.length > 0 && (
                <PropertySlider
                  properties={featuredProperties}
                  title="Propiedades Destacadas"
                  icon={Award}
                  slidesPerView={3}
                  viewAllLink={`/propiedades/constructora/${username}`}
                  usePropertyCard={true}
                  onFavoriteUpdate={(id, status) => {
                    setFeaturedProperties(prev => 
                      prev.map(p => p.id === id ? {...p, isFavorite: status} : p)
                    );
                  }}
                />
              )}
              
              {/* Recent Properties */}
              {recentProperties.length > 0 && (
                <PropertySlider
                  properties={recentProperties}
                  title="Propiedades Recientes"
                  icon={Calendar}
                  slidesPerView={3}
                  viewAllLink={`/propiedades/constructora/${username}`}
                  usePropertyCard={true}
                  onFavoriteUpdate={(id, status) => {
                    setRecentProperties(prev => 
                      prev.map(p => p.id === id ? {...p, isFavorite: status} : p)
                    );
                  }}
                />
              )}
            </motion.div>
          )}

          {activeTab === 'proyectos' && (
            <motion.div
              key="proyectos"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-8"
            >
              {projects.length > 0 ? (
                <>
                  {projects.filter(p => p.estado_proyecto === 'planos').length > 0 && (
                    <PropertySlider
                      properties={projects.filter(p => p.estado_proyecto === 'planos')}
                      title="Proyectos en Planos"
                      icon={TrendingUp}
                      slidesPerView={2}
                      onFavoriteUpdate={(id, status) => {
                        setProjects(prev => 
                          prev.map(p => p.id === id ? {...p, isFavorite: status} : p)
                        );
                      }}
                    />
                  )}
                  
                  {projects.filter(p => p.estado_proyecto === 'lanzamiento').length > 0 && (
                    <PropertySlider
                      properties={projects.filter(p => p.estado_proyecto === 'lanzamiento')}
                      title="Proyectos en Lanzamiento"
                      icon={TrendingUp}
                      slidesPerView={2}
                      onFavoriteUpdate={(id, status) => {
                        setProjects(prev => 
                          prev.map(p => p.id === id ? {...p, isFavorite: status} : p)
                        );
                      }}
                    />
                  )}
                  
                  {projects.filter(p => p.estado_proyecto === 'construccion').length > 0 && (
                    <PropertySlider
                      properties={projects.filter(p => p.estado_proyecto === 'construccion')}
                      title="Proyectos en Construcción"
                      icon={Hammer}
                      slidesPerView={2}
                      onFavoriteUpdate={(id, status) => {
                        setProjects(prev => 
                          prev.map(p => p.id === id ? {...p, isFavorite: status} : p)
                        );
                      }}
                    />
                  )}
                  
                  {projects.filter(p => p.estado_proyecto === 'terminado').length > 0 && (
                    <PropertySlider
                      properties={projects.filter(p => p.estado_proyecto === 'terminado')}
                      title="Proyectos Terminados"
                      icon={Building2}
                      slidesPerView={2}
                      onFavoriteUpdate={(id, status) => {
                        setProjects(prev => 
                          prev.map(p => p.id === id ? {...p, isFavorite: status} : p)
                        );
                      }}
                    />
                  )}
                  
                  <motion.div 
                    className="flex justify-center mt-8"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    <ActionButton
                      variant="primary"
                      size="md"
                      icon={ExternalLink}
                      onClick={() => navigate(`/proyectos/constructora/${username}`)}
                    >
                      Ver Todos los Proyectos
                    </ActionButton>
                  </motion.div>
                </>
              ) : (
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl dark:shadow-black/50 p-8 border border-amber-100 dark:border-gray-700">
                  <div className="text-center py-16">
                    <motion.div 
                      className="bg-amber-50 dark:bg-amber-900/30 w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 260, damping: 20 }}
                    >
                      <Castle className="w-10 h-10 text-amber-500 dark:text-amber-400" />
                    </motion.div>
                    <motion.p 
                      className="text-gray-500 dark:text-gray-400 text-lg"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1, transition: { delay: 0.2 } }}
                    >
                      No hay proyectos disponibles en este momento.
                    </motion.p>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {activeTab === 'propiedades' && (
            <motion.div
              key="propiedades"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-8"
            >
              {properties.length > 0 && (
                <PropertySlider
                  properties={properties}
                  title="Propiedades en Venta"
                  icon={Building2}
                  slidesPerView={3}
                  usePropertyCard={true}
                  onFavoriteUpdate={(id, status) => {
                    setProperties(prev => 
                      prev.map(p => p.id === id ? {...p, isFavorite: status} : p)
                    );
                  }}
                />
              )}
              
              {rentals.length > 0 && (
                <PropertySlider
                  properties={rentals}
                  title="Propiedades en Alquiler"
                  icon={Home}
                  slidesPerView={3}
                  usePropertyCard={true}
                  onFavoriteUpdate={(id, status) => {
                    setRentals(prev => 
                      prev.map(p => p.id === id ? {...p, isFavorite: status} : p)
                    );
                  }}
                />
              )}
              
              {properties.length === 0 && rentals.length === 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl dark:shadow-black/50 p-8 border border-amber-100 dark:border-gray-700">
                  <div className="text-center py-16">
                    <motion.div 
                      className="bg-amber-50 dark:bg-amber-900/30 w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 260, damping: 20 }}
                    >
                      <Building2 className="w-10 h-10 text-amber-500 dark:text-amber-400" />
                    </motion.div>
                    <motion.p 
                      className="text-gray-500 dark:text-gray-400 text-lg"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1, transition: { delay: 0.2 } }}
                    >
                      No hay propiedades disponibles en este momento.
                    </motion.p>
                  </div>
                </div>
              )}

              {(properties.length > 0 || rentals.length > 0) && (
                <motion.div 
                  className="flex justify-center mt-8"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <ActionButton
                    variant="primary"
                    size="md"
                    icon={ExternalLink}
                    onClick={() => navigate(`/propiedades/constructora/${username}`)}
                  >
                    Ver Todas las Propiedades
                  </ActionButton>
                </motion.div>
              )}
            </motion.div>
          )}

          {activeTab === 'reseñas' && (
            <motion.div
              key="reseñas"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl dark:shadow-black/50 p-6 md:p-8 border border-amber-100 dark:border-gray-700">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-amber-600 to-yellow-600 rounded-xl">
                      <Star className="w-5 h-5 text-white" />
                    </div>
                    <h2 className="text-xl md:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-amber-700 to-yellow-800 dark:from-amber-400 dark:to-yellow-500">
                      Reseñas de Clientes
                    </h2>
                  </div>
                  
                  {reviews.length > 0 && (
                    <select 
                      value={reviewFilter} 
                      onChange={(e) => setReviewFilter(e.target.value)}
                      className="px-4 py-2 border-2 border-amber-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200
                                focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent
                                transition-all duration-300 w-full md:w-auto
                                [&>option]:bg-white [&>option]:dark:bg-gray-800 [&>option]:text-gray-900 [&>option]:dark:text-gray-100"
                    >
                      <option value="highest">Calificaciones más altas</option>
                      <option value="lowest">Calificaciones más bajas</option>
                      <option value="newest">Más recientes</option>
                    </select>
                  )}
                </div>
                
                {reviews.length > 0 ? (
                  <motion.div 
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                    variants={staggerContainer}
                    initial="hidden"
                    animate="visible"
                  >
                    {reviews
                      .sort((a, b) => {
                        if (reviewFilter === 'highest') return b.rating - a.rating;
                        if (reviewFilter === 'lowest') return a.rating - b.rating;
                        return new Date(b.fecha) - new Date(a.fecha);
                      })
                      .map((review) => (
                        <motion.div key={review.id} variants={fadeInUp}>
                          <ReviewCard review={review} />
                        </motion.div>
                      ))}
                  </motion.div>
                ) : (
                  <div className="text-center py-16">
                    <motion.div 
                      className="bg-amber-50 dark:bg-amber-900/30 w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 260, damping: 20 }}
                    >
                      <Star className="w-10 h-10 text-amber-500 dark:text-amber-400" />
                    </motion.div>
                    <motion.h3 
                      className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-3"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1, transition: { delay: 0.2 } }}
                    >
                      Sin reseñas aún
                    </motion.h3>
                    <motion.p 
                      className="text-gray-500 dark:text-gray-400 mb-6"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1, transition: { delay: 0.3 } }}
                    >
                      ¡Sé el primero en compartir tu experiencia!
                    </motion.p>
                    {!isOwnProfile && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1, transition: { delay: 0.4 } }}
                      >
                        <ActionButton
                          variant="primary"
                          size="md"
                          icon={Star}
                          onClick={handleRateClick}
                        >
                          Escribir reseña
                        </ActionButton>
                      </motion.div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {activeTab === 'servicios' && (
            <motion.div
              key="servicios"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl dark:shadow-black/50 p-6 md:p-8 border border-amber-100 dark:border-gray-700">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-gradient-to-br from-amber-600 to-yellow-600 rounded-xl">
                    <Hammer className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl md:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-amber-700 to-yellow-800 dark:from-amber-400 dark:to-yellow-500">Nuestros Servicios</h2>
                </div>
                
                <motion.div 
                  className="grid grid-cols-1 md:grid-cols-2 gap-6"
                  variants={staggerContainer}
                  initial="hidden"
                  animate="visible"
                >
                  {Array.isArray(userData?.servicios) && userData.servicios.length > 0 ? (
                    userData.servicios.map((servicio) => (
                      <motion.div key={servicio} variants={fadeInUp}>
                        <ServiceCard 
                          title={servicio}
                          description={`Ofrecemos servicios profesionales de ${servicio.toLowerCase()} con los más altos estándares de calidad y atención al detalle, garantizando resultados excepcionales para cada proyecto.`}
                          icon={getServiceIcon(servicio)}
                        />
                      </motion.div>
                    ))
                  ) : (
                    defaultServices.map((service) => (
                      <motion.div key={service.title} variants={fadeInUp}>
                        <ServiceCard 
                          title={service.title}
                          description={service.description}
                          icon={service.icon}
                        />
                      </motion.div>
                    ))
                  )}
                </motion.div>
              </div>
            </motion.div>
          )}

          {activeTab === 'ubicación' && (
            <motion.div
              key="ubicación"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-8"
            >
              <motion.div 
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl dark:shadow-black/50 p-8 border border-amber-100 dark:border-gray-700"
                variants={fadeInUp}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-gradient-to-br from-amber-600 to-yellow-600 rounded-xl">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl md:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-amber-700 to-yellow-800 dark:from-amber-400 dark:to-yellow-500">Ubicación de la Sede</h2>
                </div>
                
                <div className="mb-6">
                  <div className="flex items-start gap-3 p-4 bg-amber-50/50 dark:bg-amber-900/20 rounded-lg mb-4 border border-amber-100 dark:border-amber-800">
                    <MapPin className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-1" />
                    <p className="text-gray-700 dark:text-gray-300">
                      {userData?.direccion_sede || 'Ubicación no especificada'}
                    </p>
                  </div>
                </div>
                
                <div className="h-[500px] rounded-xl overflow-hidden shadow-lg border border-amber-200 dark:border-gray-600">
                  <iframe
                    src={`https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_API_KEY}&q=${encodeURIComponent(userData?.direccion_sede || 'Piura, Perú')}`}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Ubicación de la sede"
                  />
                </div>
              </motion.div>

              <motion.div 
                className="grid grid-cols-1 md:grid-cols-3 gap-8"
                variants={staggerContainer}
                initial="hidden"
                animate="visible"
              >
                <BusinessHours 
                  horarios={userData?.horario_atencion || {
                    lunes_viernes: { inicio: '09:00', fin: '18:00' },
                    sabado: { inicio: '09:00', fin: '13:00' },
                    domingo: { inicio: '00:00', fin: '00:00' }
                  }}
                />
                
                <motion.div 
                  className="md:col-span-2 bg-white dark:bg-gray-800 rounded-2xl shadow-xl dark:shadow-black/50 p-8 border border-amber-100 dark:border-gray-700"
                  variants={fadeInUp}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-gradient-to-br from-amber-600 to-yellow-600 rounded-xl">
                      <Phone className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200">Contacto</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {userData?.telefono && (
                      <motion.a 
                        href={`tel:${userData.telefono}`}
                        className="flex items-center gap-4 p-4 bg-gradient-to-br from-white to-amber-50/30 dark:from-gray-800 dark:to-gray-900 rounded-xl
                                  border border-amber-100/50 dark:border-gray-700 shadow-md dark:shadow-black/30 hover:shadow-lg dark:hover:shadow-black/50 transition-all duration-300"
                        variants={cardHover}
                        initial="rest"
                        whileHover="hover"
                      >
                        <div className="p-3 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-full">
                          <Phone className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Teléfono</p>
                          <p className="text-lg font-medium text-gray-800 dark:text-gray-200">{userData.telefono}</p>
                        </div>
                      </motion.a>
                    )}
                    
                    {userData?.email && (
                      <motion.a 
                        href={`mailto:${userData.email}`}
                        className="flex items-center gap-4 p-4 bg-gradient-to-br from-white to-amber-50/30 dark:from-gray-800 dark:to-gray-900 rounded-xl
                                  border border-amber-100/50 dark:border-gray-700 shadow-md dark:shadow-black/30 hover:shadow-lg dark:hover:shadow-black/50 transition-all duration-300"
                        variants={cardHover}
                        initial="rest"
                        whileHover="hover"
                      >
                        <div className="p-3 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-full">
                          <Mail className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Email</p>
                          <p className="text-lg font-medium text-gray-800 dark:text-gray-200">{userData.email}</p>
                        </div>
                      </motion.a>
                    )}
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Rating Modal */}
      <RatingModal 
        isOpen={isRatingModalOpen}
        onClose={() => setIsRatingModalOpen(false)}
        onSubmit={handleRatingSubmit}
        userType="constructora"
      />
    </div>
  );
};

export default ConsProfile;